/**
 * EmailVerificationCard — Shown in Settings → Informations du compte.
 * Lets the user:
 *  - See current email verification status
 *  - Request a fresh 6-digit code (resend via EmailJS)
 *  - Paste the code received by email to verify
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Mail, ShieldCheck, AlertCircle, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const EmailVerificationCard = () => {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/auth/me/email/status');
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requestCode = async () => {
    setRequesting(true);
    try {
      const r = await axios.post('/api/auth/me/email/request-code');
      if (r.data?.sent) {
        toast.success(t('emailVerif.sentToast') || 'Code envoyé par email !');
      } else if (r.data?.already_verified) {
        toast.info(t('emailVerif.alreadyVerified') || 'Email déjà vérifié');
      } else {
        toast.warning(t('emailVerif.sentManualNeeded') || "Le code a été généré, mais l'envoi automatique a échoué. Contactez le support.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setRequesting(false);
    }
  };

  const verify = async () => {
    const clean = code.replace(/\s/g, '');
    if (!/^\d{6}$/.test(clean)) {
      toast.error(t('emailVerif.codeFormat') || 'Code à 6 chiffres requis');
      return;
    }
    setVerifying(true);
    try {
      await axios.post('/api/auth/me/email/verify-code', { code: clean });
      toast.success(t('emailVerif.verifiedToast') || 'Email vérifié ✓');
      setCode('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code invalide');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-3" data-testid="email-verif-card">
        <Loader2 className="w-4 h-4 animate-spin text-white/60" />
        <span className="text-sm text-white/60">{t('common.loading') || 'Chargement…'}</span>
      </div>
    );
  }

  const verified = status?.email_verified;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="email-verif-card">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${verified ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
          {verified ? <ShieldCheck className="w-5 h-5 text-emerald-400" /> : <Mail className="w-5 h-5 text-amber-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">{status?.email}</div>
          {verified ? (
            <div className="text-xs text-emerald-300 flex items-center gap-1 mt-0.5">
              <CheckCircle2 className="w-3 h-3" /> {t('emailVerif.verified') || 'Email vérifié'}
            </div>
          ) : (
            <div className="text-xs text-amber-300 mt-0.5">
              {t('emailVerif.unverified') || 'Email non vérifié'} · {`Vous avez ${status?.grace_period_days || 90} jours pour vérifier`}
            </div>
          )}
        </div>
      </div>

      {!verified && (
        <>
          <div className="text-xs text-white/60 mb-3">
            {t('emailVerif.helper') || "Collez ici le code à 6 chiffres reçu par email pour valider votre adresse. Code valable 48 h."}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="123456"
              className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-white text-center tracking-[0.5em] font-mono text-lg focus:outline-none focus:border-purple-400/50"
              data-testid="email-verif-code-input"
            />
            <button
              type="button"
              onClick={verify}
              disabled={verifying || code.length !== 6}
              className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              data-testid="email-verif-submit-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {t('emailVerif.validate') || 'Valider'}
            </button>
          </div>

          <button
            type="button"
            onClick={requestCode}
            disabled={requesting}
            className="text-xs text-purple-300 hover:text-purple-200 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="email-verif-resend-btn"
          >
            {requesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {t('emailVerif.resend') || 'Renvoyer le code'}
          </button>

          {status?.blocked && (
            <div className="mt-3 p-2.5 rounded-lg bg-red-500/15 border border-red-400/30 text-red-300 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{t('emailVerif.blocked') || 'Votre compte est bloqué car votre email n\'a pas été vérifié dans les 90 jours suivant l\'inscription. Vérifiez votre email pour le réactiver.'}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailVerificationCard;
